/**
 * Soul Profile Drawer
 *
 * Shows the user's current SOUL settings (persona, priorities, scope, etc.)
 * and lets them edit or reset their profile. Also surfaces any
 * auto-evolved priorities detected from usage patterns.
 */

import { useState, useCallback } from "react";
import { Drawer, Button, Tag, Input, message, Popconfirm } from "antd";
import {
	CloseOutlined,
	EditOutlined,
	CheckOutlined,
	UndoOutlined,
	UserOutlined,
	AimOutlined,
	GlobalOutlined,
	ThunderboltOutlined,
	EyeOutlined,
	ToolOutlined,
	RiseOutlined,
	TeamOutlined,
	DollarOutlined,
	BarChartOutlined,
	SafetyCertificateOutlined,
	BulbOutlined,
	HeartOutlined,
	AlertOutlined,
	FileTextOutlined,
	PieChartOutlined,
	BellOutlined,
} from "@ant-design/icons";
import type {
	PersonaType,
	PriorityType,
	ScopeType,
	InfoStyleType,
	CreateSoulPayload,
} from "@/services/soul-service";
import { updateUserSoul, createUserSoul } from "@/services/soul-service";
import { useSoulStore } from "@/stores/soul-store";
import "./SoulProfile.scss";

// ─── Label Maps ───

const PERSONA_MAP: Record<PersonaType, { label: string; icon: React.ReactNode }> = {
	facility_manager: { label: "Facility Manager", icon: <ToolOutlined /> },
	executive: { label: "Executive", icon: <RiseOutlined /> },
	general_manager: { label: "General Manager", icon: <TeamOutlined /> },
	location_worker: { label: "Location Worker", icon: <UserOutlined /> },
	finance: { label: "Finance", icon: <DollarOutlined /> },
	analyst: { label: "Analyst", icon: <BarChartOutlined /> },
	other: { label: "Other", icon: <BulbOutlined /> },
};

const PRIORITY_MAP: Record<PriorityType, { label: string; icon: React.ReactNode }> = {
	uptime: { label: "Equipment Uptime", icon: <ThunderboltOutlined /> },
	cost_control: { label: "Cost Control", icon: <DollarOutlined /> },
	compliance: { label: "Compliance & Regulations", icon: <SafetyCertificateOutlined /> },
	safety: { label: "Safety", icon: <AlertOutlined /> },
	energy_efficiency: { label: "Energy Efficiency", icon: <BulbOutlined /> },
	tenant_satisfaction: { label: "Tenant / Customer Satisfaction", icon: <HeartOutlined /> },
};

const SCOPE_MAP: Record<ScopeType, string> = {
	single_location: "A single location",
	few_locations: "A few locations",
	regional: "A region",
	national: "Nationwide",
	corporate_all: "The entire organization",
};

const INFO_STYLE_MAP: Record<InfoStyleType, { label: string; icon: React.ReactNode }> = {
	quick_summaries: { label: "Quick Summaries", icon: <AimOutlined /> },
	detailed_reports: { label: "Detailed Reports", icon: <FileTextOutlined /> },
	visual_charts: { label: "Visual Charts & Graphs", icon: <PieChartOutlined /> },
	just_alerts: { label: "Just Alerts", icon: <BellOutlined /> },
};

const ALL_PERSONAS: PersonaType[] = ["facility_manager", "executive", "general_manager", "location_worker", "finance", "analyst", "other"];
const ALL_PRIORITIES: PriorityType[] = ["uptime", "cost_control", "compliance", "safety", "energy_efficiency", "tenant_satisfaction"];
const ALL_SCOPES: ScopeType[] = ["single_location", "few_locations", "regional", "national", "corporate_all"];
const ALL_INFO_STYLES: InfoStyleType[] = ["quick_summaries", "detailed_reports", "visual_charts", "just_alerts"];

// ─── Component ───

interface SoulProfileProps {
	open: boolean;
	onClose: () => void;
	onReset: () => void;
}

const SoulProfile = ({ open, onClose, onReset }: SoulProfileProps) => {
	const { soul, setSoul } = useSoulStore();
	const [editingField, setEditingField] = useState<string | null>(null);
	const [isSaving, setIsSaving] = useState(false);

	// Edit state
	const [editPersona, setEditPersona] = useState<PersonaType | null>(null);
	const [editPriorities, setEditPriorities] = useState<PriorityType[]>([]);
	const [editScope, setEditScope] = useState<ScopeType | null>(null);
	const [editChallenge, setEditChallenge] = useState("");
	const [editInfoStyle, setEditInfoStyle] = useState<InfoStyleType | null>(null);

	const startEditing = useCallback((field: string) => {
		if (!soul) return;
		switch (field) {
			case "persona":
				setEditPersona(soul.persona);
				break;
			case "priorities":
				setEditPriorities([...soul.priorities]);
				break;
			case "scope":
				setEditScope(soul.scope || null);
				break;
			case "challenge":
				setEditChallenge(soul.currentChallenge || "");
				break;
			case "infoStyle":
				setEditInfoStyle(soul.infoStyle || null);
				break;
		}
		setEditingField(field);
	}, [soul]);

	const cancelEditing = useCallback(() => {
		setEditingField(null);
	}, []);

	const togglePriority = (key: PriorityType) => {
		setEditPriorities(prev => {
			if (prev.includes(key)) return prev.filter(p => p !== key);
			if (prev.length >= 3) return prev;
			return [...prev, key];
		});
	};

	const saveField = useCallback(async (field: string) => {
		if (!soul) return;
		setIsSaving(true);

		let payload: Partial<CreateSoulPayload> = {};
		switch (field) {
			case "persona":
				if (!editPersona) return;
				payload = { persona: editPersona };
				break;
			case "priorities":
				if (editPriorities.length === 0) return;
				payload = { priorities: editPriorities };
				break;
			case "scope":
				if (!editScope) return;
				payload = { scope: editScope };
				break;
			case "challenge":
				payload = { currentChallenge: editChallenge };
				break;
			case "infoStyle":
				if (!editInfoStyle) return;
				payload = { infoStyle: editInfoStyle };
				break;
		}

		try {
			const updated = await updateUserSoul(payload);
			if (updated) {
				setSoul(updated);
				message.success("Profile updated");
			} else {
				message.error("Failed to update");
			}
		} catch {
			message.error("Failed to update");
		} finally {
			setIsSaving(false);
			setEditingField(null);
		}
	}, [soul, editPersona, editPriorities, editScope, editChallenge, editInfoStyle, setSoul]);

	const handleReset = useCallback(() => {
		onClose();
		onReset();
	}, [onClose, onReset]);

	if (!soul) return null;

	return (
		<Drawer
			title={null}
			placement="right"
			onClose={onClose}
			open={open}
			width={400}
			className="soul-profile-drawer"
			closable={false}
		>
			<div className="sp-header">
				<h2 className="sp-title">My AI Profile</h2>
				<Button type="text" icon={<CloseOutlined />} onClick={onClose} className="sp-close-btn" />
			</div>

			<p className="sp-subtitle">
				This profile personalizes how the AI interacts with you.
			</p>

			<div className="sp-sections">
				{/* Persona */}
				<div className="sp-section">
					<div className="sp-section-header">
						<span className="sp-section-label">Role / Persona</span>
						{editingField !== "persona" && (
							<Button type="text" size="small" icon={<EditOutlined />} onClick={() => startEditing("persona")} className="sp-edit-btn" />
						)}
					</div>
					{editingField === "persona" ? (
						<div className="sp-edit-area">
							<div className="sp-chip-grid">
								{ALL_PERSONAS.map(key => (
									<button
										key={key}
										type="button"
										className={`sp-chip ${editPersona === key ? "selected" : ""}`}
										onClick={() => setEditPersona(key)}
									>
										{PERSONA_MAP[key].icon}
										<span>{PERSONA_MAP[key].label}</span>
									</button>
								))}
							</div>
							<div className="sp-edit-actions">
								<Button size="small" onClick={cancelEditing}>Cancel</Button>
								<Button size="small" type="primary" icon={<CheckOutlined />} loading={isSaving} onClick={() => saveField("persona")}>Save</Button>
							</div>
						</div>
					) : (
						<div className="sp-value">
							{PERSONA_MAP[soul.persona]?.icon}
							<span>{PERSONA_MAP[soul.persona]?.label || soul.persona}</span>
							{soul.personaOther && <span className="sp-other">({soul.personaOther})</span>}
						</div>
					)}
				</div>

				{/* Priorities */}
				<div className="sp-section">
					<div className="sp-section-header">
						<span className="sp-section-label">Top Priorities</span>
						{editingField !== "priorities" && (
							<Button type="text" size="small" icon={<EditOutlined />} onClick={() => startEditing("priorities")} className="sp-edit-btn" />
						)}
					</div>
					{editingField === "priorities" ? (
						<div className="sp-edit-area">
							<div className="sp-chip-grid">
								{ALL_PRIORITIES.map(key => (
									<button
										key={key}
										type="button"
										className={`sp-chip ${editPriorities.includes(key) ? "selected" : ""}`}
										onClick={() => togglePriority(key)}
									>
										{PRIORITY_MAP[key].icon}
										<span>{PRIORITY_MAP[key].label}</span>
									</button>
								))}
							</div>
							<p className="sp-hint">{editPriorities.length}/3 selected</p>
							<div className="sp-edit-actions">
								<Button size="small" onClick={cancelEditing}>Cancel</Button>
								<Button size="small" type="primary" icon={<CheckOutlined />} loading={isSaving} disabled={editPriorities.length === 0} onClick={() => saveField("priorities")}>Save</Button>
							</div>
						</div>
					) : (
						<div className="sp-tags">
							{soul.priorities.map(p => (
								<Tag key={p} className="sp-tag">
									{PRIORITY_MAP[p]?.icon} {PRIORITY_MAP[p]?.label || p}
								</Tag>
							))}
							{soul.priorities.length === 0 && <span className="sp-empty">Not set</span>}
						</div>
					)}
				</div>

				{/* Evolved Priorities */}
				{soul.evolvedPriorities && soul.evolvedPriorities.length > 0 && (
					<div className="sp-section sp-evolved">
						<div className="sp-section-header">
							<span className="sp-section-label">Detected Interests</span>
							<span className="sp-section-hint">from your usage</span>
						</div>
						<div className="sp-tags">
							{soul.evolvedPriorities.map(p => (
								<Tag key={p} className="sp-tag sp-tag-evolved">
									{PRIORITY_MAP[p]?.icon} {PRIORITY_MAP[p]?.label || p}
								</Tag>
							))}
						</div>
					</div>
				)}

				{/* Scope */}
				<div className="sp-section">
					<div className="sp-section-header">
						<span className="sp-section-label">Scope</span>
						{editingField !== "scope" && (
							<Button type="text" size="small" icon={<EditOutlined />} onClick={() => startEditing("scope")} className="sp-edit-btn" />
						)}
					</div>
					{editingField === "scope" ? (
						<div className="sp-edit-area">
							<div className="sp-chip-list">
								{ALL_SCOPES.map(key => (
									<button
										key={key}
										type="button"
										className={`sp-chip-row ${editScope === key ? "selected" : ""}`}
										onClick={() => setEditScope(key)}
									>
										<GlobalOutlined />
										<span>{SCOPE_MAP[key]}</span>
									</button>
								))}
							</div>
							<div className="sp-edit-actions">
								<Button size="small" onClick={cancelEditing}>Cancel</Button>
								<Button size="small" type="primary" icon={<CheckOutlined />} loading={isSaving} onClick={() => saveField("scope")}>Save</Button>
							</div>
						</div>
					) : (
						<div className="sp-value">
							<GlobalOutlined />
							<span>{soul.scope ? SCOPE_MAP[soul.scope] || soul.scope : "Not set"}</span>
						</div>
					)}
				</div>

				{/* Current Challenge */}
				<div className="sp-section">
					<div className="sp-section-header">
						<span className="sp-section-label">Current Challenge</span>
						{editingField !== "challenge" && (
							<Button type="text" size="small" icon={<EditOutlined />} onClick={() => startEditing("challenge")} className="sp-edit-btn" />
						)}
					</div>
					{editingField === "challenge" ? (
						<div className="sp-edit-area">
							<Input.TextArea
								value={editChallenge}
								onChange={(e) => setEditChallenge(e.target.value)}
								placeholder="What's your biggest challenge right now?"
								maxLength={500}
								autoSize={{ minRows: 2, maxRows: 4 }}
								className="sp-textarea"
							/>
							<div className="sp-edit-actions">
								<Button size="small" onClick={cancelEditing}>Cancel</Button>
								<Button size="small" type="primary" icon={<CheckOutlined />} loading={isSaving} onClick={() => saveField("challenge")}>Save</Button>
							</div>
						</div>
					) : (
						<p className="sp-text-value">
							{soul.currentChallenge || <span className="sp-empty">Not set</span>}
						</p>
					)}
				</div>

				{/* Info Style */}
				<div className="sp-section">
					<div className="sp-section-header">
						<span className="sp-section-label">Info Preference</span>
						{editingField !== "infoStyle" && (
							<Button type="text" size="small" icon={<EditOutlined />} onClick={() => startEditing("infoStyle")} className="sp-edit-btn" />
						)}
					</div>
					{editingField === "infoStyle" ? (
						<div className="sp-edit-area">
							<div className="sp-chip-grid">
								{ALL_INFO_STYLES.map(key => (
									<button
										key={key}
										type="button"
										className={`sp-chip ${editInfoStyle === key ? "selected" : ""}`}
										onClick={() => setEditInfoStyle(key)}
									>
										{INFO_STYLE_MAP[key].icon}
										<span>{INFO_STYLE_MAP[key].label}</span>
									</button>
								))}
							</div>
							<div className="sp-edit-actions">
								<Button size="small" onClick={cancelEditing}>Cancel</Button>
								<Button size="small" type="primary" icon={<CheckOutlined />} loading={isSaving} onClick={() => saveField("infoStyle")}>Save</Button>
							</div>
						</div>
					) : (
						<div className="sp-value">
							{soul.infoStyle && INFO_STYLE_MAP[soul.infoStyle] ? (
								<>
									{INFO_STYLE_MAP[soul.infoStyle].icon}
									<span>{INFO_STYLE_MAP[soul.infoStyle].label}</span>
								</>
							) : (
								<span className="sp-empty">Not set</span>
							)}
						</div>
					)}
				</div>
			</div>

			{/* Reset */}
			<div className="sp-footer">
				<Popconfirm
					title="Reset your AI profile?"
					description="This will clear your current settings and restart the setup."
					onConfirm={handleReset}
					okText="Reset"
					cancelText="Cancel"
					okButtonProps={{ danger: true }}
				>
					<Button type="text" icon={<UndoOutlined />} className="sp-reset-btn" danger>
						Reset Profile
					</Button>
				</Popconfirm>
			</div>
		</Drawer>
	);
};

export { SoulProfile };
